import { c as _c } from "react/compiler-runtime";
import { parseAsString, useQueryStates } from 'nuqs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCartDrawer } from '@sola/ark-skippy/contexts/cart-drawer.tsx';
import { useCart } from '@sola/cart-sdk/store.tsx';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { ChildGiftLimitItem } from '@sola/schema/dotcom/proxy-api/giftLimits.ts';
import { type GraphQLCommitment, GraphQLCommitmentType, type GraphQLSupporterGiftLimits } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useContentSettings } from '@sola/sdg-sdk/hooks.ts';
import { useReadyForQueries, useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { beneficiaryExitsQuery, commitmentsQuery, giftLimitsQuery } from '@sola/supporter-sdk/queries.ts';
import { isSponsorshipCommitment } from '@sola/supporter-sdk/utils/commitments.ts';
import type { GiftOption } from '../../types/gifts.ts';
import { resolveExitedChildFinalOptions } from '../../utils/gift-availability/exited-final-gifts.ts';
import { formatGiftOptions } from '../../utils/gift-availability/gift-availability.ts';
import { buildGiftCartItemPayload } from '../../utils/gift-cart/gift-cart-payload.ts';
import type { GiftOptionsSubmitData } from '../gift-options-dialog/gift-options-dialog.tsx';
export const getCardElementKey = (participantId: string, giftType: string) => `${giftType.toLowerCase()}-${participantId}`;
const parseGiftType = (giftTypeString: string | null): GiftTypeEnum | null => {
  if (!giftTypeString) {
    return null;
  }
  return Object.values(GiftTypeEnum).find(value => value.toLowerCase() === giftTypeString.toLowerCase()) ?? null;
};
export interface SelectedGiftOptions {
  commitment: GraphQLCommitment;
  giftOption: GiftOption;
  giftType: GiftTypeEnum;
}
export interface RemovedGift {
  cartItemId: string;
  childName: string;
  giftType: GiftTypeEnum;
}
const getSelectedGiftTypeLimits = (giftLimits: GraphQLSupporterGiftLimits | undefined, selectedGiftOptions: SelectedGiftOptions | null): ChildGiftLimitItem | undefined => {
  if (!giftLimits || !selectedGiftOptions) {
    return undefined;
  }
  const {
    commitment,
    giftOption,
    giftType
  } = selectedGiftOptions;
  const childGlobalId = commitment.participant?.globalId ?? commitment.globalId;
  const giftLimitGroup = giftType === GiftTypeEnum.Center ? giftLimits.projects.find(project => project.id === giftOption.centerId) : giftLimits.children.find(child => child.id === childGlobalId);
  return giftLimitGroup?.giftTypes[giftType] ??
  // TODO: Is it wise to ever return the defaults for a Final Gift?
  giftLimits.defaults[giftType] ?? undefined;
};
export const useChildGiftsLogic = t0 => {
  const $ = _c(70);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const childCenterMap = t1;
  const {
    t
  } = useTranslation("my");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      id: parseAsString,
      type: parseAsString
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [t3, setActiveQuery] = useQueryStates(t2);
  const {
    id: activeChildId,
    type: activeGiftType
  } = t3;
  const {
    data: contentSettings,
    isLoading: isContentSettingsLoading
  } = useContentSettings();
  const {
    globalId
  } = useReadyForQueries();
  const {
    actions: t4
  } = useCart("actions");
  const {
    addItems,
    removeItem,
    updateItem
  } = t4;
  const {
    cart
  } = useCart("cart");
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [giftLimitsQuery, commitmentsQuery, beneficiaryExitsQuery];
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    data: t6,
    isLoading,
    isPending,
    isError
  } = useSupporterQuery(t5);
  const [giftLimits, commitments, beneficiaryExits] = t6;
  const isGiftListLoading = !isError && (!giftLimits || !commitments || !beneficiaryExits) && (isPending || isLoading);
  const hasCommitments = !!commitments?.length;
  cart?.items;
  let t7;
  if ($[4] !== beneficiaryExits || $[5] !== cart?.items || $[6] !== childCenterMap || $[7] !== commitments || $[8] !== giftLimits) {
    bb0: {
      const base = formatGiftOptions(giftLimits, commitments, cart?.items ?? [], childCenterMap);
      if (!giftLimits || !beneficiaryExits?.length) {
        t7 = {
          childGiftOptions: base,
          baseFinalCount: base.Final.length
        };
        break bb0;
      }
      const activeGlobalIds = new Set((commitments ?? []).values().filter(isSponsorshipCommitment).map(_temp).filter(_temp2));
      const exited = resolveExitedChildFinalOptions(giftLimits, beneficiaryExits, cart?.items ?? [], activeGlobalIds);
      const t8 = {
        ...base,
        Final: [...base.Final, ...exited]
      };
      let t9;
      if ($[10] !== base || $[11] !== t8) {
        t9 = {
          childGiftOptions: t8,
          baseFinalCount: base.Final.length
        };
        $[10] = base;
        $[11] = t8;
        $[12] = t9;
      } else {
        t9 = $[12];
      }
      t7 = t9;
    }
    $[4] = beneficiaryExits;
    $[5] = cart?.items;
    $[6] = childCenterMap;
    $[7] = commitments;
    $[8] = giftLimits;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const {
    childGiftOptions,
    baseFinalCount
  } = t7;
  const shouldShowFinalGiftSection = isGiftListLoading || childGiftOptions.Final.length > 0;
  let t8;
  bb1: {
    if (isGiftListLoading) {
      t8 = true;
      break bb1;
    }
    let t9;
    if ($[13] !== commitments) {
      t9 = commitments ?? [];
      $[13] = commitments;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    let t10;
    if ($[15] !== t9) {
      t10 = t9.filter(_temp3);
      $[15] = t9;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    const activeChildSponsorshipCount = t10.length;
    t8 = baseFinalCount !== activeChildSponsorshipCount;
  }
  const shouldShowOtherGiftSections = t8;
  const [selectedGiftOptions, setSelectedGiftOptions] = useState(null);
  const {
    openCartDrawer,
    clearCartDrawerAlert
  } = useCartDrawer();
  let t9;
  bb2: {
    if (!activeChildId || !activeGiftType || isLoading) {
      t9 = null;
      break bb2;
    }
    let t10;
    if ($[17] !== activeGiftType) {
      t10 = parseGiftType(activeGiftType);
      $[17] = activeGiftType;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    const giftType = t10;
    if (!giftType) {
      t9 = null;
      break bb2;
    }
    let t11;
    if ($[19] !== childGiftOptions || $[20] !== giftType) {
      t11 = childGiftOptions?.[giftType] ?? [];
      $[19] = childGiftOptions;
      $[20] = giftType;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    const list = t11;
    let t12;
    if ($[22] !== activeChildId || $[23] !== list) {
      let t13;
      if ($[25] !== activeChildId) {
        t13 = t14 => {
          const {
            commitment: commitment_1
          } = t14;
          return commitment_1.participant?.globalId === activeChildId;
        };
        $[25] = activeChildId;
        $[26] = t13;
      } else {
        t13 = $[26];
      }
      t12 = list.find(t13);
      $[22] = activeChildId;
      $[23] = list;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    const match = t12;
    let t13;
    if ($[27] !== giftType || $[28] !== match) {
      t13 = match ? {
        commitment: match.commitment,
        giftOption: match.giftOption,
        giftType
      } : null;
      $[27] = giftType;
      $[28] = match;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    t9 = t13;
  }
  const querySelection = t9;
  const activeSelection = selectedGiftOptions ?? querySelection;
  let t10;
  if ($[30] !== activeSelection || $[31] !== giftLimits) {
    t10 = getSelectedGiftTypeLimits(giftLimits, activeSelection);
    $[30] = activeSelection;
    $[31] = giftLimits;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const selectedGiftTypeLimits = t10;
  let t11;
  if ($[33] !== setActiveQuery) {
    t11 = selection => {
      setSelectedGiftOptions(selection);
      setActiveQuery({
        id: selection.commitment.participant?.globalId ?? null,
        type: selection.giftType.toLowerCase()
      });
    };
    $[33] = setActiveQuery;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const handleGiftOptionsOpen = t11;
  let t12;
  if ($[35] !== activeSelection || $[36] !== addItems || $[37] !== clearCartDrawerAlert || $[38] !== globalId || $[39] !== openCartDrawer || $[40] !== updateItem) {
    t12 = async t13 => {
      const {
        amount: t14,
        frequency
      } = t13;
      const amount = t14 === undefined ? 0 : t14;
      clearCartDrawerAlert();
      if (!activeSelection) {
        throw new Error("Unable to add gift without selected gift options");
      }
      const {
        commitment: commitment_2,
        giftOption,
        giftType: giftType_0
      } = activeSelection;
      const cartItemId = giftOption.cartItem?.cartId;
      const isExistingItem = !!cartItemId;
      if (isExistingItem) {
        const updateErrors = await updateItem(cartItemId, {
          unitPrice: amount,
          commitmentFrequency: frequency
        });
        if (updateErrors?.length) {
          throw new Error("Unable to update gift in cart");
        }
        openCartDrawer();
        return;
      }
      const giftCartItem = buildGiftCartItemPayload({
        amount,
        centerId: giftOption.centerId,
        commitment: commitment_2,
        frequency,
        giftType: giftType_0
      });
      const addItemErrors = await addItems({
        items: giftCartItem,
        supporterId: globalId
      });
      if (addItemErrors?.length) {
        throw new Error("Unable to add gift to cart");
      }
      openCartDrawer();
    };
    $[35] = activeSelection;
    $[36] = addItems;
    $[37] = clearCartDrawerAlert;
    $[38] = globalId;
    $[39] = openCartDrawer;
    $[40] = updateItem;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const handleGiftOptionsSubmit = t12;
  let t13;
  if ($[42] !== openCartDrawer || $[43] !== removeItem || $[44] !== t) {
    t13 = async t14 => {
      const {
        cartItemId: cartItemId_0,
        childName,
        giftType: giftType_1
      } = t14;
      const removeItemErrors = await removeItem(cartItemId_0);
      if (removeItemErrors?.length) {
        throw new Error("Unable to remove gift from cart");
      }
      openCartDrawer({
        alert: {
          variant: "positive",
          message: t("my:gifts.removeGiftSuccess.message", {
            childName,
            giftType: t(`my:gifts.typeLabels.${giftType_1.toLowerCase()}`)
          })
        }
      });
    };
    $[42] = openCartDrawer;
    $[43] = removeItem;
    $[44] = t;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  const handleGiftRemove = t13;
  let t14;
  if ($[46] !== setActiveQuery) {
    t14 = open => {
      if (open) {
        return;
      }
      setSelectedGiftOptions(null);
      setActiveQuery({
        id: null,
        type: null
      });
    };
    $[46] = setActiveQuery;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const handleGiftOptionsOpenChange = t14;
  const lastScrolledKeyRef = useRef(null);
  let t15;
  let t16;
  if ($[48] !== activeChildId || $[49] !== querySelection || $[50] !== selectedGiftOptions) {
    t15 = () => {
      if (!querySelection || selectedGiftOptions || !activeChildId) {
        return;
      }
      const key = getCardElementKey(activeChildId, querySelection.giftType);
      if (lastScrolledKeyRef.current === key) {
        return;
      }
      const activeCard = document.querySelector(`#${CSS.escape(key)}`);
      if (!activeCard) {
        return;
      }
      lastScrolledKeyRef.current = key;
      activeCard.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    };
    t16 = [querySelection, selectedGiftOptions, activeChildId];
    $[48] = activeChildId;
    $[49] = querySelection;
    $[50] = selectedGiftOptions;
    $[51] = t15;
    $[52] = t16;
  } else {
    t15 = $[51];
    t16 = $[52];
  }
  useEffect(t15, t16);
  let t17;
  if ($[53] !== activeSelection || $[54] !== childGiftOptions || $[55] !== contentSettings || $[56] !== giftLimits || $[57] !== handleGiftOptionsOpen || $[58] !== handleGiftOptionsOpenChange || $[59] !== handleGiftOptionsSubmit || $[60] !== handleGiftRemove || $[61] !== hasCommitments || $[62] !== isContentSettingsLoading || $[63] !== isError || $[64] !== isGiftListLoading || $[65] !== isLoading || $[66] !== selectedGiftTypeLimits || $[67] !== shouldShowFinalGiftSection || $[68] !== shouldShowOtherGiftSections) {
    t17 = {
      isLoading,
      isGiftListLoading,
      isError,
      isContentSettingsLoading,
      hasCommitments,
      contentSettings,
      giftLimits,
      childGiftOptions,
      shouldShowFinalGiftSection,
      shouldShowOtherGiftSections,
      activeSelection,
      selectedGiftTypeLimits,
      handleGiftOptionsOpen,
      handleGiftOptionsSubmit,
      handleGiftRemove,
      handleGiftOptionsOpenChange
    };
    $[53] = activeSelection;
    $[54] = childGiftOptions;
    $[55] = contentSettings;
    $[56] = giftLimits;
    $[57] = handleGiftOptionsOpen;
    $[58] = handleGiftOptionsOpenChange;
    $[59] = handleGiftOptionsSubmit;
    $[60] = handleGiftRemove;
    $[61] = hasCommitments;
    $[62] = isContentSettingsLoading;
    $[63] = isError;
    $[64] = isGiftListLoading;
    $[65] = isLoading;
    $[66] = selectedGiftTypeLimits;
    $[67] = shouldShowFinalGiftSection;
    $[68] = shouldShowOtherGiftSections;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  return t17;
};
function _temp(commitment) {
  return commitment.participant?.globalId;
}
function _temp2(id) {
  return !!id;
}
function _temp3(commitment_0) {
  return commitment_0.commitmentType === GraphQLCommitmentType.ChildSponsorship && !!commitment_0.participant?.globalId;
}